import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { flip, offset, type UseVirtualFloatingOptions } from '@platejs/floating';
import { getLinkAttributes } from '@platejs/link';
import {
	FloatingLinkUrlInput,
	type LinkFloatingToolbarState,
	useFloatingLinkEdit,
	useFloatingLinkEditState,
	useFloatingLinkInsert,
	useFloatingLinkInsertState,
} from '@platejs/link/react';

import { Flex } from 'antd';
import { Edit, ExternalLink, Link, Unlink } from 'lucide-react';
import type { TLinkElement } from 'platejs';
import { KEYS } from 'platejs';
import { useEditorRef, useEditorSelection, useFormInputProps, usePluginOption } from 'platejs/react';

import { FLOATING_ICON_SIZE, ToolbarButton } from 'features/Editor';

import toolbarStyles from '../FloatingToolbar/FloatingToolbar.module.scss';

import styles from './LinkToolbar.module.scss';

export function LinkFloatingToolbar({ state }: { state?: LinkFloatingToolbarState }) {
	const activeCommentId = usePluginOption({ key: KEYS.comment }, 'activeId');
	const activeSuggestionId = usePluginOption({ key: KEYS.suggestion }, 'activeId');

	const floatingOptions: UseVirtualFloatingOptions = useMemo(
		() => ({
			middleware: [
				offset(8),
				flip({
					fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
					padding: 12,
				}),
			],
			placement: activeSuggestionId || activeCommentId ? 'top-start' : 'bottom-start',
		}),
		[activeCommentId, activeSuggestionId],
	);

	const insertState = useFloatingLinkInsertState({
		...state,
		floatingOptions: {
			...floatingOptions,
			...state?.floatingOptions,
		},
	});
	const { hidden, props: insertProps, ref: insertRef } = useFloatingLinkInsert(insertState);

	const editState = useFloatingLinkEditState({
		...state,
		floatingOptions: {
			...floatingOptions,
			...state?.floatingOptions,
		},
	});
	const { editButtonProps, props: editProps, ref: editRef, unlinkButtonProps } = useFloatingLinkEdit(editState);
	const inputProps = useFormInputProps({
		preventDefaultOnEnterKeydown: true,
	});

	const { t } = useTranslation();

	if (hidden) return null;

	const input = (
		<Flex align="center" gap={4} style={{ padding: '0 6px' }} {...inputProps}>
			<Link size={FLOATING_ICON_SIZE} />

			<FloatingLinkUrlInput className={styles.input} placeholder={t('Ссылка')} data-plate-focus />
		</Flex>
	);

	const editContent = editState.isEditing ? (
		input
	) : (
		<Flex align="center" gap={2}>
			<ToolbarButton icon={<Edit size={FLOATING_ICON_SIZE} {...editButtonProps} />} tooltip={t('Редактировать')} />
			<LinkOpenButton />
			<ToolbarButton {...unlinkButtonProps} icon={<Unlink size={FLOATING_ICON_SIZE} />} tooltip={t('Удалить ссылку')} />
		</Flex>
	);

	return (
		<>
			<div ref={insertRef} className={toolbarStyles.floatingToolbar} {...insertProps}>
				{input}
			</div>

			<div ref={editRef} className={toolbarStyles.floatingToolbar} {...editProps}>
				{editContent}
			</div>
		</>
	);
}

function LinkOpenButton() {
	const { t } = useTranslation();
	const editor = useEditorRef();
	const selection = useEditorSelection();

	const attributes = useMemo(
		() => {
			const entry = editor.api.node<TLinkElement>({
				match: { type: editor.getType(KEYS.link) },
			});
			if (!entry) {
				return {};
			}
			const [element] = entry;
			return getLinkAttributes(editor, element);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[editor, selection],
	);

	return (
		<ToolbarButton
			icon={<ExternalLink size={FLOATING_ICON_SIZE} />}
			tooltip={t('Открыть ссылку')}
			onMouseOver={(e) => {
				e.stopPropagation();
			}}
			target="_blank"
			{...attributes}
		/>
	);
}
